const SETTLED_REVENUE_SOURCES = new Set(["manual", "import", "system"]);

function localDateTimeKey(date, time = "00:00") {
  return `${date || ""}T${time || "00:00"}`;
}

function zonedNowKey(now, timeZone) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);
    const value = Object.fromEntries(parts.map(({ type, value: part }) => [type, part]));
    return `${value.year}-${value.month}-${value.day}T${value.hour}:${value.minute}`;
  } catch {
    return zonedNowKey(now, "UTC");
  }
}

function localEndKey(show) {
  if (show.end_date && show.end_time) return localDateTimeKey(show.end_date, show.end_time);
  if (show.end_time) {
    const start = localDateTimeKey(show.date, show.start_time || "00:00");
    const end = localDateTimeKey(show.date, show.end_time);
    if (end < start) {
      const nextDay = new Date(`${show.date}T${show.end_time}:00Z`);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      return `${nextDay.toISOString().slice(0, 10)}T${show.end_time}`;
    }
    return end;
  }
  const start = new Date(`${show.date}T${show.start_time || "00:00"}:00Z`);
  start.setUTCMinutes(start.getUTCMinutes() + Math.max(1, Number(show.duration_min) || 60));
  return `${start.toISOString().slice(0, 10)}T${start.toISOString().slice(11, 16)}`;
}

export function classifyShowPeriod(show, now = new Date()) {
  if (["completed", "cancelled"].includes(show.status)) return "past";
  const current = zonedNowKey(now, show.timezone || "UTC");
  const start = localDateTimeKey(show.date, show.start_time || "00:00");
  const end = localEndKey(show);
  if (!start.startsWith("T") && current < start) return "upcoming";
  return current <= end ? "current" : "past";
}

export function filterScheduleShows(shows, filters = {}) {
  const location = String(filters.location || "").trim().toLowerCase();
  return (shows || []).filter((show) => {
    if (filters.organizerId && show.organizer_crm_id !== filters.organizerId) return false;
    if (filters.modelId && !(show.cast || []).some((person) => person.crm_id === filters.modelId)) return false;
    if (filters.status && show.status !== filters.status) return false;
    if (filters.from && show.date < filters.from) return false;
    if (filters.to && show.date > filters.to) return false;
    if (location && ![show.location_name, show.city, show.country].some((value) => String(value || "").toLowerCase().includes(location))) return false;
    return true;
  });
}

export function isSettledLiveShowEntry(entry) {
  return String(entry?.revenue_type || "").toLowerCase() === "live_show"
    && String(entry?.platform || "").toLowerCase() === "live_show"
    && SETTLED_REVENUE_SOURCES.has(String(entry?.source || "").toLowerCase())
    && Number.isFinite(Number(entry?.amount));
}

export function showRevenue(show, entries = []) {
  const linkedIds = new Set(show.revenue_entry_ids || []);
  const received = (entries || []).filter((entry) => linkedIds.has(entry.id) && isSettledLiveShowEntry(entry));
  const receivedByCurrency = received.reduce((totals, entry) => {
    const currency = String(entry.currency || "USD").toUpperCase();
    totals[currency] = Math.round(((totals[currency] || 0) + Number(entry.amount)) * 100) / 100;
    return totals;
  }, {});
  const currency = String(show.currency || "EUR").toUpperCase();
  const receivedAmount = receivedByCurrency[currency] || 0;
  const confirmed = Number(show.confirmed_revenue) || 0;
  return {
    expected: Number(show.expected_earnings) || 0,
    confirmed,
    receivedByCurrency,
    received: receivedAmount,
    outstanding: Math.max(0, confirmed - receivedAmount),
    currency,
    currencyMismatch: Object.keys(receivedByCurrency).some((value) => value !== currency),
  };
}

export function aggregateShowRevenue(shows, entries, dimension, peopleById = {}) {
  const groups = new Map();
  (shows || []).forEach((show) => {
    const keyValues = dimension === "organizer"
      ? [show.organizer_crm_id || "unassigned"]
      : dimension === "model"
        ? [...new Set((show.cast || []).map((person) => person.crm_id))]
        : [show.id];
    const revenue = showRevenue(show, entries);
    keyValues.forEach((key) => {
      const group = groups.get(key) || {
        id: key,
        label: dimension === "event" ? show.title : peopleById[key]?.name || "Unassigned",
        events: 0,
        expectedByCurrency: {},
        confirmedByCurrency: {},
        receivedByCurrency: {},
      };
      group.events += 1;
      group.expectedByCurrency[revenue.currency] = (group.expectedByCurrency[revenue.currency] || 0) + revenue.expected;
      group.confirmedByCurrency[revenue.currency] = (group.confirmedByCurrency[revenue.currency] || 0) + revenue.confirmed;
      Object.entries(revenue.receivedByCurrency).forEach(([currency, amount]) => {
        group.receivedByCurrency[currency] = (group.receivedByCurrency[currency] || 0) + amount;
      });
      groups.set(key, group);
    });
  });
  return { groups: [...groups.values()], notAdditive: dimension === "model" };
}
